import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import api from "@/lib/api";
import { fmtCurrency, fmtDate } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Download, List } from "lucide-react";

const csvCell = (v) => {
  let s = v == null ? "" : String(v);
  if (/^[=+\-@\t\r]/.test(s)) s = `'${s}`;
  return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
};

function exportCsv(seg, rows) {
  const head = ["First name", "Last name", "Phone", "Email", "Last visit", "Days since visit", "Visits", "Lifetime billed", "Next appointment"];
  const lines = [head.join(","), ...rows.map((p) => [p.first_name, p.last_name, p.phone, p.email, p.last_visit, p.days_since_visit,
    p.visit_count || 0, p.lifetime_value || 0, p.next_appointment].map(csvCell).join(","))];
  const url = URL.createObjectURL(new Blob([lines.join("\n")], { type: "text/csv" }));
  Object.assign(document.createElement("a"), { href: url, download: `recall-${seg.id}.csv` }).click();
  URL.revokeObjectURL(url);
}

const Row = ({ p }) => (
  <tr data-testid={`segment-patient-${p.id}`}>
    <td className="px-3 py-2 whitespace-nowrap font-medium text-slate-800">{p.first_name} {p.last_name}</td>
    <td className="px-3 py-2 whitespace-nowrap text-slate-600">{p.phone || "—"}</td>
    <td className="px-3 py-2 whitespace-nowrap text-slate-600">{p.last_visit ? fmtDate(p.last_visit) : "—"}</td>
    <td className="px-3 py-2 whitespace-nowrap text-slate-600">{p.days_since_visit ?? "—"}</td>
    <td className="px-3 py-2 whitespace-nowrap text-slate-600">{p.visit_count || 0}</td>
    <td className="px-3 py-2 whitespace-nowrap text-slate-600">{fmtCurrency(p.lifetime_value || 0)}</td>
    <td className="px-3 py-2 whitespace-nowrap text-slate-600">{p.next_appointment ? fmtDate(p.next_appointment) : "None booked"}</td>
  </tr>
);

export const SegmentPatients = ({ seg }) => {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const { data, isLoading } = useQuery({ queryKey: ["segment-patients", seg.id], enabled: open,
    queryFn: async () => (await api.get(`/campaigns/segments/${seg.id}/patients`)).data });
  const all = data?.patients || [];
  const s = q.trim().toLowerCase();
  const rows = s ? all.filter((p) => `${p.first_name} ${p.last_name} ${p.phone || ""}`.toLowerCase().includes(s)) : all;
  const shown = rows.slice(0, 500);

  return (
    <>
      <button data-testid={`segment-view-${seg.id}`} onClick={() => setOpen(true)} disabled={!seg.count}
        className="mt-3 inline-flex items-center gap-1 text-xs font-semibold text-[#0B1F44] hover:underline disabled:text-slate-300 disabled:no-underline">
        <List className="h-3.5 w-3.5" /> List patients
      </button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid={`segment-dialog-${seg.id}`} className="sm:max-w-5xl max-h-[85vh] flex flex-col">
          <DialogHeader>
            <DialogTitle className="font-heading">{seg.label}</DialogTitle>
            <DialogDescription>{seg.description}</DialogDescription>
          </DialogHeader>
          <div className="flex flex-wrap items-center gap-3">
            <span data-testid={`segment-dialog-count-${seg.id}`} className="text-sm font-semibold text-slate-700">
              {isLoading ? "Loading…" : `${rows.length} patients${rows.length > 500 ? " (first 500 shown, export for all)" : ""}`}
            </span>
            <Input data-testid="segment-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search name or phone" className="h-8 w-56 ml-auto" />
            <Button data-testid="segment-export" size="sm" variant="outline" className="h-8" disabled={!rows.length} onClick={() => exportCsv(seg, rows)}>
              <Download className="h-4 w-4 mr-1.5" />Export CSV
            </Button>
          </div>
          <div className="overflow-auto border border-slate-200 rounded-lg">
            <table className="w-full min-w-[820px] text-sm">
              <thead className="bg-slate-50 text-[11px] uppercase tracking-wide text-slate-400 text-left sticky top-0">
                <tr><th className="px-3 py-2">Patient</th><th className="px-3 py-2">Phone</th><th className="px-3 py-2">Last visit</th>
                  <th className="px-3 py-2">Days since</th><th className="px-3 py-2">Visits</th><th className="px-3 py-2">Lifetime billed</th><th className="px-3 py-2">Next appt</th></tr>
              </thead>
              <tbody className="divide-y divide-slate-100">{shown.map((p) => <Row key={p.id} p={p} />)}</tbody>
            </table>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};
